// /src/Components/Clients/AdminTemplate.jsx
import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import { Building2, Filter, RefreshCcw, Eye, Trash2, Phone } from "lucide-react";
import whatsapp from "../../Assets/whatsapp_background.jpg"

import useWhatsapp from "../Services/WhatsappServices/useWhatsapp";

const PAGE_SIZE = 100;

export default function AdminTemplate() {
  const { getWhatsappNumbers, getWhatsappTemplatesV1, deleteWhatsappTemplate } = useWhatsapp();

  const { clientId } = useParams(); // userid for API
  const navigate = useNavigate();
  const { api } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [phoneId, setPhoneId] = useState("");

  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");

  // preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const loadNumbers = async () => {
    if (!clientId) return;

    try {
      const result =
        await getWhatsappNumbers(clientId);

      if (result.success) {
        const list = result.data;

        setNumbers(list);

        // pick first by default
        if (list.length) {
          setPhoneId(list[0].phone_no_id);
        }
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error loading WhatsApp numbers");
    }
  };


  const loadTemplates = async () => {
    if (!phoneId) return;

    setLoading(true);

    try {
      const result =
        await getWhatsappTemplatesV1({
          userid: clientId,
          phoneNumberId: phoneId,
        });

      if (result.success) {
        setTemplates(result.data);
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error loading templates");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneId]);

  const filtered = useMemo(() => {
    const s = (v) => String(v ?? "").toLowerCase();
    const ql = q.trim().toLowerCase();
    return templates.filter(
      (t) =>
        !ql ||
        s(t.id).includes(ql) ||
        s(t.name).includes(ql) ||
        s(t.status).includes(ql) ||
        s(t.language).includes(ql) ||
        s(t.category).includes(ql)
    );
  }, [templates, q]);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const clear = () => {
    setQ("");
    setPage(1);
  };

const decodeBase64 = (value) => {
  try {

    return decodeURIComponent(
      escape(atob(value || ""))
    );

  } catch {

    return value || "";
  }
};

const openPreview = (templateName) => {

  const t = templates.find(
    (x) =>
      x.name === templateName ||
      x.id === templateName
  );

  if (!t) {
    setPreviewTemplate(null);
    setPreviewOpen(true);
    return;
  }

  const updatedTemplate = {
    ...t,

    components:
      t.components?.map((c) => ({

        ...c,

        text: decodeBase64(c.text),

        buttons:
          c.buttons?.map((b) => ({
            ...b,
            text: decodeBase64(b.text),
          })) || [],

      })) || [],
  };

  setPreviewTemplate(updatedTemplate);

  setPreviewOpen(true);
};

  const askDelete = (templateName) => {
    setToDelete(templateName);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!toDelete) return;

    setConfirmBusy(true);

    try {
      const result =
        await deleteWhatsappTemplate({
          userid: clientId,
          templateId: toDelete,
          secret: "string",
        });

      if (result.success) {
        toast.success(result.message);

        // Optimistic update
        setTemplates((x) =>
          x.filter(
            (t) =>
              t.name !== toDelete &&
              t.id !== toDelete
          )
        );
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      toast.error("Error deleting template");
    } finally {
      setConfirmBusy(false);
      setConfirmOpen(false);
      setToDelete(null);
    }
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />
      {/* header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Templates — {clientId}</h1>
            <p className="text-sm text-gray-500">Check statuses, preview, and manage</p>
          </div>
        </div>
        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          onClick={() => loadTemplates()}
          disabled={loading || !phoneId}
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {/* controls */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={16} /> Controls
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">WhatsApp Number</div>
            <select
              className="h-10 w-80 rounded-xl border bg-white px-3 text-sm outline-none"
              value={phoneId}
              onChange={(e) => setPhoneId(e.target.value)}
            >
              <option value="">Select number</option>
              {numbers.map((n) => (
                <option key={n.phone_no_id} value={n.phone_no_id}>
                  {n.phone_no_id} — ({n.phone_no})
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <input
              className="h-10 w-72 rounded-xl border px-3 text-sm outline-none"
              placeholder="id / name / status / language / category"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
            />
          </label>

          <div className="ml-auto flex gap-2">
            <button className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50" onClick={clear}>
              Clear
            </button>
            <button className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white" disabled>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full table-fixed text-left text-sm">
            {/* <colgroup>
              <col className="w-16" />
              <col className="w-[18%]" />
              <col className="w-[26%]" />
              <col className="w-[16%]" />
              <col className="w-[16%]" />
              <col className="w-[14%]" />
              <col className="w-[14%]" />
            </colgroup> */}
            <thead className="bg-[#01C37D] text-white">
              <tr className="whitespace-nowrap">
                {["#", "Template ID", "Template Name", "Language", "Category", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-2 font-semibold border-r-2 border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    {phoneId ? "No templates found." : "Select a WhatsApp number to load templates."}
                  </td>
                </tr>
              )}

              {pageRows.map((t, i) => (
                <tr key={`${t.id}-${i}`} className="border-t align-top">
                  <td className="px-4 py-2 border-r-2 border-gray-300 ">{(page - 1) * PAGE_SIZE + i + 1}</td>
                  <td className="px-4 py-2 border-r-2 border-gray-300">{t.id}</td>
                  <td className="px-4 py-2 border-r-2 border-gray-300 uppercase">{t.name}</td>
                  <td className="px-4 py-2 border-r-2 border-gray-300 uppercase">{t.language}</td>
                  <td className="px-4 py-2 border-r-2 border-gray-300">{t.category}</td>
                  <td className="px-4 py-2 border-r-2 border-gray-300">
                    {t.status === "APPROVED" ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {t.status}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                        onClick={() => openPreview(t.name)}
                      >
                        <Eye size={14} /> Preview
                      </button>
                      <button
                        className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                        onClick={() => navigate(`/admin/templatesview/${clientId}/${t.name}`)}
                      >
                        <Phone size={14} /> View
                      </button>
                      <button
                        className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                        onClick={() => askDelete(t.name)}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
            <span className="text-sm text-gray-600">Page <b>{page}</b> of <b>{totalPages}</b></span>
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <Transition show={previewOpen} as={Fragment}>
        <Dialog onClose={() => setPreviewOpen(false)} className="relative z-50">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold">Message Preview</Dialog.Title>
                <div className="mt-3 rounded-xl border p-3">
                  {!previewTemplate ? (
                    <div className="py-10 text-center text-sm text-gray-500">No template found.</div>
                  ) : (
                    <TemplatePreview template={previewTemplate} />
                  )}
                </div>
                <div className="mt-4 flex justify-end">
                  <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setPreviewOpen(false)}>Close</button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirm */}
      <Transition show={confirmOpen} as={Fragment}>
        <Dialog onClose={() => (!confirmBusy && setConfirmOpen(false))} className="relative z-50">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold">Delete Template?</Dialog.Title>
                <p className="mt-2 text-sm text-gray-700">This action cannot be undone.</p>
                <div className="mt-4 flex justify-end gap-2">
                  <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setConfirmOpen(false)} disabled={confirmBusy}>Cancel</button>
                  <button className="h-9 rounded-xl bg-rose-600 px-3 text-sm font-medium text-white hover:bg-rose-700" onClick={doDelete} disabled={confirmBusy}>
                    {confirmBusy ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

function TemplatePreview({ template }) {

  const header =
    template.components?.find(
      (c) => c.type === "HEADER"
    )?.text;

  const body =
    template.components?.find(
      (c) => c.type === "BODY"
    )?.text;

  const footer =
    template.components?.find(
      (c) => c.type === "FOOTER"
    )?.text;

  const buttons =
    template.components?.find(
      (c) => c.type === "BUTTONS"
    )?.buttons || [];

  const media =
    template.components?.find((c) =>
      ["IMAGE", "VIDEO", "DOCUMENT"]
        .includes(c.format)
    );

  const fill = (text) =>
    (text || "").replace(
      /{{(\d+)}}/g,
      (_, i) =>
        (
          ["John Doe", "Messaging"][
            i - 1
          ] || ""
        )
    );

  return (

    <div className="rounded-2xl bg-gray-100 p-4"
    style={{
                      backgroundImage:
                        `url(${whatsapp})`,
                    }}
                  >
    

      <div className="mx-auto max-w-sm rounded-2xl bg-white shadow-sm">

        {/* media */}
        {media?.format && (
          <div className="border-b px-4 py-3 text-xs text-gray-500">
            Header contains {media.format}
          </div>
        )}

        {/* content */}
        <div className="space-y-3 p-4">

          {header && (
            <div className="text-sm font-semibold text-gray-900">
              {fill(header)}
            </div>
          )}

          {body && (
            <div className="whitespace-pre-wrap text-sm leading-6 text-gray-800">
              {fill(body)}
            </div>
          )}

          {footer && (
            <div className="pt-1 text-xs text-gray-500">
              {fill(footer)}
            </div>
          )}

        </div>

        {/* buttons */}
        {!!buttons.length && (
          <div className="border-t">

            {buttons.map((b, i) => (
              <button
                key={i}
                className="w-full border-b px-4 py-3 text-sm font-medium text-[#00a884] last:border-b-0 hover:bg-gray-50"
              >
                {b.text}
              </button>
            ))}

          </div>
        )}

      </div>

    </div>
  );
}
